/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { Box, Button, Grid, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import { RootState, useAppDispatch } from "@store/store";
import CustomUploadFileV2 from "@components/CustomUploadFileV2";
import { useLazyGetBanksByCodeQuery } from "@api/catalogs";
import {
  useDeleteBankAccountMutation,
  useLazyGetBankAccountsQuery,
  usePostBankAccountStatementMutation,
  useDeleteBankAccountStatementMutation,
  usePostBankAccountsMutation,
  usePutApplicationCompletedMutation,
  usePutBankAccountMutation,
} from "@api/applications";
import { useLazyGetDocumentAppIdQuery, usePatchDocumentMutation } from "@api/documents";
import { CustomDialog } from "@components/CustomDialog";
import { InactivityHandler } from "@components/InactivityHandler";
import { UUID } from "crypto";
import { DocumentResponse, ApiError } from "@interfaces/response";
import { PayloadBankAccount } from "@interfaces/request";
import { DOCUMENTS_CATALOG, INVALID_BANK_ACCOUNT_CODE } from "@helpers/constantes";
import CustomRightImage from "@components/CustomRightImagev2";
import { setModalErrorVisible } from "@store/slices/appSlice";
import * as Sentry from "@sentry/react";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { BankAccountFormItem } from "@interfaces/catalogs";
import { identifyBankAccounts, createEmptyBankAccount, getFormikArrayError } from "@helpers/funciones";

const MAX_ACCOUNTS_ALLOWED = 5;

const DEFAULT_NUMBER_BANK_STATEMENTS = 3;

const InformacionBancaria = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const personalData = useSelector((state: RootState) => state.register.personal_data);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [loadingBankName, setLoadingBankName] = useState<boolean>(false);
  const [loadingBankStatement, setLoadingBankStatement] = useState<boolean>(false);
  const [accountIndexToDelete, setAccountIndexToDelete] = useState<number | undefined>(undefined);

  const [triggerGetBanks] = useLazyGetBanksByCodeQuery();
  const [getBankAccounts] = useLazyGetBankAccountsQuery();
  const [deleteBankAccount] = useDeleteBankAccountMutation();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerPutBankAccount] = usePutBankAccountMutation();
  const [triggerPostBankAccount] = usePostBankAccountsMutation();
  const [triggerPatchDocument] = usePatchDocumentMutation();
  const [putAapplicationCompleted] = usePutApplicationCompletedMutation();
  const [triggerPostBankAccountStatement] = usePostBankAccountStatementMutation();
  const [triggerDeleteBankAccountStatement] = useDeleteBankAccountStatementMutation();

  const handleOnClickRestart = () => {
    navigate("/producto/creditosimple/avance_expediente_digital");
  };

  const getNumberStatements = (): number => {
    return (
      interfaceConfig.formularios?.informacion_bancaria_empresa?.numero_estados_cuenta || DEFAULT_NUMBER_BANK_STATEMENTS
    );
  };

  const formik = useFormik({
    initialValues: {
      accounts: [createEmptyBankAccount({ number_statements: getNumberStatements() })],
    },
    validationSchema: yup.object().shape({
      accounts: yup.array().of(
        yup.object().shape({
          clabe: yup
            .string()
            .min(18, "Formato incorrecto, debe tener 18 dígitos")
            .required("Campo requerido.")
            .test("unique", "La cuenta bancaria ya se encuentra registrada.", function (value) {
              const num_results: number = formik.values.accounts.filter(
                (item: BankAccountFormItem) => item.clabe === value,
              ).length;
              return num_results > 1 ? false : true;
            }),
          bank_id: yup.string().required("Clabe bancaria incorrecta, validar nuevamente."),
          statements: yup.array().of(
            yup.object().shape({
              bank_statement_document_id: yup.string().required("Campo requerido."),
              document_id: yup.string().required("Campo requerido."),
              document_order: yup.number().required("Campo requerido."),
              document_value: yup.object(),
            }),
          ),
        }),
      ),
    }),
    onSubmit: async (_values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        await putAapplicationCompleted({
          id_sol: applicationData.id,
          body: {
            bank_information_status: "COMPLETO",
          },
        }).unwrap();

        handleOnClickRestart();
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "InformacionBancariaEmpresa-submit",
          },
        });
        const api_error = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const handleOnAddAccountBank = () => {
    if (formik.values.accounts.length >= MAX_ACCOUNTS_ALLOWED) return;
    formik.setFieldValue("accounts", [
      ...formik.values.accounts,
      createEmptyBankAccount({ number_statements: getNumberStatements() }),
    ]);
  };

  const getBankNameByCode = useCallback(async (bank_code: string) => {
    try {
      const bank_info = await triggerGetBanks(bank_code).unwrap();
      if (bank_info) {
        return {
          id: bank_info.id,
          name: bank_info.name,
        };
      } else {
        return null;
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaEmpresa-getBankNameByCode",
        },
      });
      return null;
    }
  }, []);

  const handleChangeCLABE = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    try {
      const { name, value } = event.target;
      const account = formik.values.accounts[index];
      const only_numbers = value.replace(/[^0-9]/g, "");
      formik.setFieldValue(name, only_numbers);

      if (only_numbers.length === 18) {
        setLoadingBankName(true);
        const bank_code = only_numbers.substring(0, 3);
        const bank_info = await getBankNameByCode(bank_code);

        if (bank_info) {
          // guardar la cuenta bancaria en la bd
          const payload: PayloadBankAccount = {
            loan_application_id: applicationData.id,
            client_details_id: personalData.id,
            person_id: null,
            account: 0,
            account_holder: "",
            clabe: only_numbers,
            bank_id: bank_info.id,
            documents: [],
          };

          if (account.id) {
            await triggerPutBankAccount({
              solicitudId: applicationData.id,
              bankAccountId: account.id,
              body: payload,
            }).unwrap();
          } else {
            const response = await triggerPostBankAccount({
              solicitudId: applicationData.id,
              body: payload,
            }).unwrap();

            formik.setFieldValue(`accounts[${index}].id`, response.id);
          }

          // setear la informacion del banco en el formulario
          formik.setFieldValue(`accounts[${index}].bank_id`, bank_info.id);
          formik.setFieldValue(`accounts[${index}].bank_name`, bank_info.name || "");
        } else {
          formik.setFieldValue(`accounts[${index}].bank_id`, "");
          formik.setFieldValue(`accounts[${index}].bank_name`, "");
        }

        setTimeout(() => {
          formik.validateForm();
          formik.setFieldTouched(`accounts[${index}].bank_id`, true);
          formik.setFieldTouched(`accounts[${index}].bank_name`, true);
        }, 500);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaEmpresa-handleChangeCLABE",
        },
      });
    } finally {
      setLoadingBankName(false);
    }
  };

  const handleOpenModalDelete = (index: number) => {
    setAccountIndexToDelete(index);
  };

  const handleOnCloseModalDelete = () => {
    setAccountIndexToDelete(undefined);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      if (accountIndexToDelete === undefined) return;

      const copy_accounts: BankAccountFormItem[] = [...formik.values.accounts];
      const account_to_delete = copy_accounts[accountIndexToDelete];

      if (!account_to_delete || !account_to_delete.id) {
        setAccountIndexToDelete(undefined);
        return;
      }

      // verificar si hay documentos validados relacionados a la cuenta bancaria
      const documents_validated = account_to_delete.statements.filter(
        (item) => item?.document_value?.validated === true,
      );

      if (documents_validated.length > 0) {
        // dispatch(setModalErrorVisible({ open: true, error: {
        //   message: "No se puede eliminar la cuenta bancaria porque hay documentos validados relacionados.",
        // } }));
        console.error("no se puede eliminar la cuenta bancaria porque hay documentos validados relacionados.");
        return;
      }

      // desactivar documentos
      for (let index = 0; index < account_to_delete.statements.length; index++) {
        const bank_statement = account_to_delete.statements[index];
        if (bank_statement && bank_statement.document_id) {
          await triggerPatchDocument({
            documentId: bank_statement.document_id as UUID,
            body: {
              status: false,
            },
          }).unwrap();
        }
      }

      // desactivar la cuenta bancaria
      await deleteBankAccount({
        solicitudId: applicationData.id,
        accountId: account_to_delete.id,
      }).unwrap();

      const new_accounts = copy_accounts.filter((_, index) => index !== accountIndexToDelete);
      formik.setFieldValue("accounts", new_accounts);
      handleOnCloseModalDelete();
    } catch (error) {
      console.error("Ha ocurrido un error al eliminar la cuenta bancaria", error);
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaEmpresa-handleConfirmDelete",
        },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUploadBankStatement = async (
    newUpload: DocumentResponse | undefined,
    accountIndex: number,
    statementIndex: number,
  ) => {
    try {
      setLoadingBankStatement(true);

      const copy_accounts: BankAccountFormItem[] = [...formik.values.accounts];
      const account: BankAccountFormItem = copy_accounts[accountIndex];
      const statement = account.statements[statementIndex];

      if (newUpload) {
        if (account.id) {
          const response = await triggerPostBankAccountStatement({
            solicitudId: applicationData.id,
            accountId: account.id,
            body: {
              loan_application_bank_account_id: account.id,
              document_id: newUpload.id,
              document_order: statementIndex + 1,
            },
          }).unwrap();

          copy_accounts[accountIndex].statements[statementIndex] = {
            bank_statement_document_id: response.id,
            document_id: newUpload.id,
            document_order: statementIndex + 1,
            document_value: newUpload,
          };

          // actualizar el formulario
          formik.setFieldValue("accounts", copy_accounts);
        }
      } else {
        if (account.id && statement.bank_statement_document_id) {
          await triggerDeleteBankAccountStatement({
            solicitudId: applicationData.id,
            accountId: account.id,
            documentId: statement.bank_statement_document_id,
          }).unwrap();

          copy_accounts[accountIndex].statements[statementIndex] = {
            bank_statement_document_id: undefined,
            document_id: undefined,
            document_order: statementIndex + 1,
            document_value: undefined,
          };

          // actualizar el formulario
          formik.setFieldValue("accounts", copy_accounts);
        }
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaEmpresa-handleUploadBankStatement",
        },
      });
    } finally {
      setLoadingBankStatement(false);
    }
  };

  const getInitialData = async () => {
    try {
      setIsLoading(true);

      const [list_bank_accounts, documents] = await Promise.all([
        getBankAccounts(applicationData.id).unwrap(),
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          clientDetailsId: personalData.id,
          status: true,
        }).unwrap(),
      ]);

      // filtrar las cuentas bancarias de la empresa
      const { company_accounts } = identifyBankAccounts({
        bank_accounts: list_bank_accounts,
        company: personalData,
      });

      const accounts: BankAccountFormItem[] = [];
      const num_statements_required = getNumberStatements();

      for (let index = 0; index < company_accounts.length; index++) {
        const account = company_accounts[index];

        const firstThree = (account.clabe || "").toString().substring(0, 3);

        if (firstThree === INVALID_BANK_ACCOUNT_CODE) {
          const cuenta_clabe = (account.clabe || "").toString().padStart(18, "0");

          const invalid_acc: BankAccountFormItem = {
            id: account.id,
            clabe: cuenta_clabe,
            bank_id: "",
            bank_name: "",
            statements: Array(num_statements_required).fill(undefined),
          };

          for (let i = 0; i < num_statements_required; i++) {
            const account_document = account.documents[i];
            if (account_document) {
              invalid_acc.statements[i] = {
                bank_statement_document_id: account_document.bank_statement_document_id,
                document_id: account_document.document_id,
                document_order: account_document.document_order || 0,
                document_value: documents.find((item) => item.id === account_document.document_id),
              };
            }
          }

          const pushIndex = accounts.length;
          accounts.push(invalid_acc);

          // Limpiar CLABE y mostrar error para que el usuario ingrese una válida
          try {
            formik.setFieldValue(`accounts[${pushIndex}].clabe`, "");
            formik.setFieldValue(`accounts[${pushIndex}].bank_name`, "");
            formik.setFieldTouched(`accounts[${pushIndex}].clabe`, true);
            formik.setFieldError(`accounts[${pushIndex}].clabe`, "Agrega una CLABE bancaria válida.");
          } catch (e) {
            console.warn("No se pudo marcar/limpiar campo en Formik:", e);
          }

          continue;
        }

        // consultar el nombre del banco
        const bank_code = (account.clabe || "").toString().substring(0, 3);
        const bank_info = await getBankNameByCode(bank_code);

        // generar el nuevo item para el formulario
        const new_accout: BankAccountFormItem = {
          id: account.id,
          clabe: account.clabe || "",
          bank_id: bank_info?.id || "",
          bank_name: bank_info?.name || "",
          statements: Array(num_statements_required).fill(undefined),
        };

        for (let index = 0; index < num_statements_required; index++) {
          const account_document = account.documents[index];
          if (account_document) {
            new_accout.statements[index] = {
              bank_statement_document_id: account_document.bank_statement_document_id,
              document_id: account_document.document_id,
              document_order: account_document.document_order || 0,
              document_value: documents.find((item) => item.id === account_document.document_id),
            };
          }
        }

        accounts.push(new_accout);
      }

      if (accounts.length) {
        formik.setFieldValue("accounts", accounts);
      }
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaEmpresa-initial_fetch",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInitialData();
  }, []);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8}>
          <InactivityHandler />
          <Box maxWidth="sm" mx="auto" mt={4} px={2}>
            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={700}
              color={theme?.palette?.primary?.dark}
            >
              Información bancaria
            </Typography>

            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.dark}
              mt={2}
            >
              <strong>1</strong> de 1
            </Typography>

            <Typography
              textAlign={"center"}
              color={theme?.palette?.primary?.main}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              mt={2}
            >
              Ingresa los datos de la cuenta en dónde concentras la mayor facturación de tu negocio, a esta cuenta
              depositaremos tu crédito
            </Typography>

            <form onSubmit={formik.handleSubmit}>
              {formik.values.accounts.map((account: BankAccountFormItem, index: number) => (
                <Box key={index} mt={index > 0 ? 4 : 0}>
                  <TextField
                    name={`accounts[${index}].clabe`}
                    label="Cuenta CLABE"
                    margin="normal"
                    type="text"
                    fullWidth
                    autoComplete="off"
                    onBlur={formik.handleBlur}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeCLABE(event, index)}
                    value={formik.values.accounts[index].clabe}
                    error={
                      Boolean(getFormikArrayError(formik, "accounts", index, "clabe")) ||
                      Boolean(getFormikArrayError(formik, "accounts", index, "bank_id"))
                    }
                    helperText={
                      getFormikArrayError(formik, "accounts", index, "clabe") ||
                      getFormikArrayError(formik, "accounts", index, "bank_id")
                    }
                    inputProps={{ maxLength: 18 }}
                    disabled={isLoading || isDeleting || formik.isSubmitting || loadingBankName}
                  />

                  <TextField
                    name={`accounts[${index}].bank_name`}
                    label="Banco"
                    margin="normal"
                    fullWidth
                    autoComplete="off"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.accounts[index].bank_name}
                    disabled={true}
                  />

                  <Box my={2}>
                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={theme?.palette?.fontWeights?.regular}
                      color={theme?.palette?.primary?.dark}
                    >
                      Debes cargar los estado de cuenta bancario de los últimos {getNumberStatements()} meses de una o
                      mas cuentas para cubrir la capacidad de pago necesaria para el crédito solicitado.
                    </Typography>
                  </Box>

                  {account.statements.map((statement, statementIndex) => (
                    <Box key={`bank_statement_${index}_${statementIndex}`}>
                      <CustomUploadFileV2
                        mt={2}
                        inputName={`bank_statement_${index}_${statementIndex}`}
                        uploadedFile={statement?.document_value}
                        setUploadedFile={(newUpload) => {
                          handleUploadBankStatement(newUpload, index, statementIndex);
                        }}
                        optionalName={`Estado de cuenta ${statementIndex + 1}`}
                        optionalDescription=""
                        metadata={{
                          fileCode: DOCUMENTS_CATALOG.estado_cuenta_bancario.code,
                          uploadType: "client",
                          clientDetailsId: personalData.id,
                        }}
                        disabled={isLoading || isDeleting || formik.isSubmitting || loadingBankStatement || !account.id}
                        required
                      />
                    </Box>
                  ))}

                  <Box mt={2} display={"flex"} flexDirection={"column"} columnGap={2} alignItems={"flex-start"}>
                    {index > 0 && (
                      <Button
                        variant="text"
                        onClick={() => handleOpenModalDelete(index)}
                        sx={{ px: 0 }}
                        disabled={isLoading || isDeleting || formik.values.accounts[index].id === undefined}
                      >
                        - Eliminar cuenta bancaria
                      </Button>
                    )}

                    <Button
                      variant="text"
                      onClick={handleOnAddAccountBank}
                      sx={{ px: 0 }}
                      disabled={
                        isLoading ||
                        isDeleting ||
                        formik.values.accounts[index].id === undefined ||
                        formik.values.accounts.length >= getNumberStatements()
                      }
                    >
                      + Agregar cuenta bancaria
                    </Button>
                  </Box>
                </Box>
              ))}

              <Box my={3} px={3}>
                <Typography
                  variant={isMobileOrTablet ? "body3" : "body2"}
                  fontWeight={600}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Si la cuenta refleja menos del 50% de los ingresos agrega otra cuenta bancaria hasta llegar a este
                  porcentaje
                </Typography>
              </Box>

              <Grid container columnSpacing={1} my={4}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="blue_outlined"
                    sx={{ padding: "0px", height: "100%" }}
                    onClick={handleOnClickRestart}
                    disabled={isLoading || formik.isSubmitting}
                  >
                    Regresar al inicio
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="default"
                    disabled={isLoading || !formik.isValid || formik.isSubmitting}
                  >
                    Continuar
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>

      <CustomDialog
        colorClose={theme?.palette?.primary?.light}
        open={accountIndexToDelete !== undefined}
        onClose={handleOnCloseModalDelete}
        itemTitle={<></>}
        itemContent={
          <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"}>
            <Typography
              textAlign={"center"}
              color={theme?.palette?.primary?.dark}
              fontWeight={600}
              variant={isMobileOrTablet ? "h5" : "h6"}
            >
              ¿Estás seguro de eliminar la cuenta bancaria?
            </Typography>
            <Box display={"flex"} columnGap={2} justifyContent={"center"} mt={3}>
              <Button
                variant="blue_outlined"
                sx={{ py: 0, px: 1, width: "110px" }}
                onClick={handleOnCloseModalDelete}
                disabled={isDeleting}
              >
                Regresar
              </Button>

              <Button
                variant="default"
                sx={{ width: "110px" }}
                onClick={() => handleConfirmDelete()}
                disabled={isDeleting}
              >
                Eliminar
              </Button>
            </Box>
          </Box>
        }
      />
    </>
  );
};

export default InformacionBancaria;
